import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, FileText, Fingerprint, Scan, Shield, UserCheck, Zap } from 'lucide-react';
import { cn } from '@/lib/utils';
import styles from './index.module.less';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useAddAccountModal } from '@/context/AddAccountModalProvider';
import { useRouter } from 'next/router';
import IndividualsCards from './IndividualsCards';

const skipIcon = (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M22.5 12.5C22.5 6.97688 18.0231 2.5 12.5 2.5C6.97688 2.5 2.5 6.97688 2.5 12.5C2.5 18.0231 6.97688 22.5 12.5 22.5C18.0231 22.5 22.5 18.0231 22.5 12.5ZM12.0288 13.8681H11C8 13.8681 7.14875 15.7087 7.14875 15C7.14875 14.2912 8 11.3006 11.5 11.3006H12.0288V8.69438L17.78 12.585L12.0288 16.4756V13.8681Z"
      fill="white"
    />
  </svg>
);

type HeroTab = 'creators' | 'individuals';

export type HeroProps = {
  data: any;
  isLogin: boolean;
  activeTab?: HeroTab;
  onTabChange?: React.Dispatch<React.SetStateAction<HeroTab>>;
};

const Hero: React.FC<HeroProps> = ({ data, isLogin, activeTab, onTabChange }) => {
  const isMobile = useIsMobile();
  const [internalTab, setInternalTab] = useState<HeroTab>('creators');
  const currentTab = activeTab ?? internalTab;
  const { t } = useTranslation();
  const titleText = data?.title ?? '';
  const highlightText = data?.highlight ?? '';
  const highlightIndex = highlightText ? titleText.indexOf(highlightText) : -1;
  const titlePrefix = highlightIndex >= 0 ? titleText.slice(0, highlightIndex) : titleText;
  const titleSuffix = highlightIndex >= 0 ? titleText.slice(highlightIndex + highlightText.length) : '';
  const activeTabData = data?.tabs?.[0];
  const handleTabChange = (tab: HeroTab) => {
    if (!onTabChange) {
      setInternalTab(tab);
      return;
    }
    onTabChange(tab);
  };

  return (
    <section className={styles.section} style={{ marginTop: !isLogin && !isMobile ? '40px' : 0 }}>
      <div className={styles.backgroundLayer}>
        <motion.div
          animate={{
            rotate: [0, 360],
            scale: [1, 1.2, 1],
          }}
          transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
          className={styles.gradientLeft}
        />
        <motion.div
          animate={{
            rotate: [360, 0],
            scale: [1, 1.3, 1],
          }}
          transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
          className={styles.gradientRight}
        />

        <div className={styles.radar}>
          <div className={cn(styles.radarRing, styles.radarRingLarge)} />
          <div className={cn(styles.radarRing, styles.radarRingMedium)} />
          <div className={cn(styles.radarRing, styles.radarRingSmall)} />
        </div>

        <div className={styles.grid} />

        <motion.div
          animate={{ y: [0, 15, 0], rotate: [0.5, -0.5, 0.5] }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
          className={styles.scanCard}
        >
          <div className={styles.scanHeader}>
            <Scan size={20} className={styles.scanIcon} />
            <div className={styles.scanBadge}>Scanning...</div>
          </div>
          <div className={styles.scanBars}>
            {[0.4, 0.7, 0.2, 0.9, 0.5, 0.8, 0.3].map((h, i) => (
              <div key={i} style={{ height: `${h * 100}%` }} className={styles.scanBar} />
            ))}
          </div>
        </motion.div>

        <motion.div
          animate={{ y: [0, -12, 0], rotate: [-0.5, 0.5, -0.5] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
          className={styles.checkCard}
        >
          <div className={styles.checkHeader}>
            <div className={styles.checkIconWrap}>
              <CheckCircle size={14} />
            </div>
            <div className={styles.checkLineShort} />
          </div>
          <div className={styles.checkLines}>
            <div className={styles.checkLine} />
            <div className={styles.checkLineHalf} />
          </div>
        </motion.div>

        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 40, repeat: Infinity, ease: 'linear' }}
          className={styles.fingerprint}
        >
          <Fingerprint size={64} strokeWidth={1} className={styles.fingerprintIcon} />
        </motion.div>

        <motion.div
          animate={{ scale: [1, 1.2, 1], opacity: [0.1, 0.3, 0.1] }}
          transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
          className={styles.zap}
        >
          <Zap size={80} strokeWidth={1} className={styles.zapIcon} />
        </motion.div>
      </div>

      <div className={styles.content}>
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className={styles.badge}>
          <div className={styles.badgeAvatars}>
            <Image src="/images/home/avatars.png" alt="avatars" width={61} height={24} />
          </div>
          {t('Protected prefix', { defaultValue: '' }) && (
            <>
              <span className={styles.text}>{t('Protected prefix', { defaultValue: '' })}</span>{' '}
            </>
          )}
          <span className={styles.badgeText}>{t('10,000+')}</span>{' '}
          <span className={styles.text}>{t('Users Protected')}</span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className={styles.title}
        >
          {titlePrefix}
          <br />
          {highlightIndex >= 0 && <span className={styles.titleAccent}>{highlightText}</span>}
          <br />
          {titleSuffix}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className={styles.description}
        >
          {data?.subTitle}
        </motion.p>

        <div className={styles.toggle}>
          <motion.div
            animate={{ x: currentTab === 'creators' ? 0 : 176 }}
            className={styles.toggleActive}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          />
          <button
            onClick={() => handleTabChange('creators')}
            className={cn(styles.toggleBtn, currentTab === 'creators' && styles.toggleBtnActive)}
          >
            <h2>{data?.tabs[0]?.title}</h2>
          </button>
          <button
            onClick={() => handleTabChange('individuals')}
            className={cn(styles.toggleBtn, currentTab === 'individuals' && styles.toggleBtnActive)}
          >
            {data?.tabs[1]?.title}
          </button>
        </div>

        <div className={styles.trustContainer}>
          {activeTabData?.imgPath && (
            <Image
              src={activeTabData.imgPath}
              alt={activeTabData.title ?? ''}
              width={32}
              height={32}
              className={styles.trustImage}
              loading="lazy"
              aria-hidden={!activeTabData.title}
            />
          )}
          <p className={styles.trustText}>{activeTabData?.desc}</p>
        </div>

        <div className={styles.cardGrid} style={{ display: currentTab === 'creators' ? undefined : 'none' }}>
          {(activeTabData?.cards ?? []).slice(0, 3).map((card: { title?: string; desc?: string }, index: number) => {
            const iconMap = [Shield, UserCheck, FileText];
            const bgClassMap = [styles.cardIconBlue, styles.cardIconPurple, styles.cardIconOrange];
            const iconColorMap = [styles.cardIconBlueColor, styles.cardIconPurpleColor, styles.cardIconOrangeColor];
            const Icon = iconMap[index] ?? Shield;
            const iconBgClass = bgClassMap[index] ?? styles.cardIconBlue;
            const iconColorClass = iconColorMap[index] ?? styles.cardIconBlueColor;
            return (
              <FeatureCard
                key={`${card?.title ?? 'card'}-${index}`}
                icon={<Icon className={cn(styles.cardIcon, iconColorClass)} size={32} />}
                iconBgClass={iconBgClass}
                title={card?.title ?? ''}
                description={card?.desc ?? ''}
                index={index}
              />
            );
          })}
        </div>
        <h2 style={{ display: 'none' }}>{data?.tabs[1]?.title}</h2>
        <div style={{ display: currentTab === 'individuals' ? undefined : 'none' }}>
          <IndividualsCards />
        </div>
      </div>
    </section>
  );
};

const FeatureCard = ({
  icon,
  iconBgClass,
  title,
  description,
  index,
}: {
  icon: React.ReactNode;
  iconBgClass: string;
  title: string;
  description: string;
  index: number;
}) => {
  const { t } = useTranslation();
  const { openAddAccountModal } = useAddAccountModal();
  const router = useRouter();
  const handleLearnMore = (index: number) => {
    if (index === 0) {
      router.push('/remove-leaked-onlyfans-content');
    } else if (index === 1) {
      router.push('/remove-fake-account');
    } else if (index === 2) {
      router.push('/dmca-takedown-service');
    }
  };
  return (
    <motion.div whileHover={{ y: -12, scale: 1.02 }} className={styles.card}>
      <div className={styles.cardBody}>
        <div className={cn(styles.cardIconWrap, iconBgClass)}>{icon}</div>
        <h3 className={styles.cardTitle}>{title}</h3>
        <p className={styles.cardDescription}>{description}</p>
      </div>
      <div className={styles.cardActions}>
        <button className={styles.primaryButton} onClick={() => openAddAccountModal?.()}>
          {skipIcon} {t('Start free')}
        </button>
        <button className={styles.secondaryButton} onClick={() => handleLearnMore(index)}>
          {t('Learn more')}
        </button>
      </div>
    </motion.div>
  );
};

export default Hero;
